import awsconfig from "../aws-exports";
import { API, graphqlOperation, Storage } from "aws-amplify";
import { createPhoto } from "../graphql/mutations";
import { photoByParentByTime } from "../graphql/queries";
import { getAccount } from "../graphql/account/account-queries";
import { getReloadYard } from "../graphql/reloadyard/custom-queries";

import {
  CLOUD_FRONT_URL,
  FilePaths,
  HelpTopicOptions,
  MAX_FILES,
  MAX_FILEs_MSG,
  MAX_FILE_SIZE,
  MAX_FILE_SIZE_MSG,
} from "./constants";
import { logError } from "./appUtils";

export const base64ToBlob = (b64Data, contentType = "", sliceSize = 512) => {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
};

export const downloadFile = (file, key) => {
  const fakeLink = document.createElement("a");
  fakeLink.style.display = "none";
  document.body.appendChild(fakeLink);

  // const buffer = Buffer.from(file.blob, "base64")
  // const blob = new Blob([buffer], {type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document"});

  const blob = file.Body;
  const filename = getKeyFilename(key);
  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    // Manage IE11+ & Edge
    window.navigator.msSaveOrOpenBlob(blob, filename);
  } else {
    fakeLink.setAttribute("href", URL.createObjectURL(blob));
    fakeLink.setAttribute("download", filename);
    fakeLink.click();
  }
};

export const getDirFileKey = (file, dir) => {
  if (!file) return;
  if (dir && window.appUser.reloadYardId)
    return `${window.appUser.reloadYardId}/${dir}/${file.name}`;
  if (dir) return `${dir}/${file.name}`;
  return `${window.appUser.reloadYardId}/${file.name}`;
};

export const getFileKey = (file, id) => {
  if (!file) return;
  if (id)
    return `${window.appUser.reloadYardId}/${id}_${new Date().getTime()}_${
      file.name
    }`;
  else
    return `${window.appUser.reloadYardId}/${new Date().getTime()}_${
      file.name
    }`;
};

export const getKeyFilename = (key) => {
  if (!key) return;
  return key.split("/").pop();
};

export const getFilePublicURL = (key) => {
  const serviceApi = API._options.aws_cloud_logic_custom.find(
    (itm) => itm.name === "transREST"
  );
  let restEndpoint = serviceApi.endpoint;
  if (!restEndpoint)
    restEndpoint = awsconfig.aws_cloud_logic_custom[0].endpoint;

  return restEndpoint + "/getFiles?key=" + key;
};

export const getPhoto = (key, setPhoto, width = 256) => {
  if (!key || !setPhoto) return;
  if (key === "PHOTO_LOADING") return "";
  const url = getPhotoUrl(key, width);
  setPhoto(url);
};

export const getPhotoUrl = (key, width = 256) => {
  if (!key) return;
  if (width > 1200) width = 1200;

  const imageRequest = {
    bucket: API._options.aws_user_files_s3_bucket,
    key: `public/${key}`,
    edits: {
      resize: {
        width: width,
        fit: "inside",
      },
    },
  };

  const encodedString = Buffer.from(JSON.stringify(imageRequest)).toString(
    "base64"
  );
  const url = `${CLOUD_FRONT_URL}/${encodedString}`;
  return url;
};

export const copyPhotos = async (sourceId, parentId, parentType) => {
  let sourcePhotos = [];
  try {
    const resp = await API.graphql(
      graphqlOperation(photoByParentByTime, { parentId: sourceId })
    );
    sourcePhotos = resp.data?.photoByParentByTime?.items || [];
  } catch (error) {
    logError("fileUtils:copyPhotos:fetchSource", error, false);
    return [];
  }
  if (!sourcePhotos || sourcePhotos.length === 0) return [];

  try {
    const parentPhotos = [];
    const promiseList = sourcePhotos.map((photo) => {
      const input = {
        ...photo,
        parentId,
        parentType,
      };
      delete input.id;
      delete input.createdAt;
      delete input.updatedAt;

      return API.graphql(graphqlOperation(createPhoto, { input }));
    });

    const results = await Promise.allSettled(promiseList);

    results.forEach((result) => {
      if (result.status === "fulfilled") {
        const newPhoto = result.value?.data?.createPhoto;
        if (newPhoto) parentPhotos.push(newPhoto);
      } else {
        const err = result.reason;
        const errorMessage =
          err?.errors?.[0]?.message ??
          (err instanceof Error ? err.message : JSON.stringify(err));
        logError("fileUtils:copyPhotos:createPhoto", errorMessage, false);
      }
    });

    return parentPhotos;
  } catch (error) {
    logError("fileUtils:copyPhotos", error, false);
    return [];
  }
};

export const handleFileChange = (source, value, form, notify) => {
  let error = null;
  debugger;

  if (value && Array.isArray(value) && value.length > 0) {
    const filesState = form.getFieldState(source);
    if (value.length > MAX_FILES) {
      error = MAX_FILEs_MSG;
      let newValue = filesState.value.filter(
        (itm) => itm.size <= MAX_FILE_SIZE || itm.rawFile?.size <= MAX_FILE_SIZE
      );
      newValue = newValue.slice(0, MAX_FILES);
      form.change(source, newValue);
    } else {
      const newValue = filesState.value.filter(
        (itm) =>
          itm.key ||
          itm.size <= MAX_FILE_SIZE ||
          itm.rawFile?.size <= MAX_FILE_SIZE
      );
      if (newValue.length !== value.length) error = MAX_FILE_SIZE_MSG;
      form.change(source, newValue);
    }
  } else if (value && typeof value === "object") {
    if (value?.size > MAX_FILE_SIZE) {
      error = MAX_FILE_SIZE_MSG;
      form.change(source, null);
    }
  }

  if (error) notify(error, "warning");
};

export const removeFile = async (record, data, source) => {
  if (record && record[source]) {
    const key = record[source].key;
    if (key) await Storage.remove(key);
    if (data) data[source] = null;
  }
};

export const removeFiles = async (record, source) => {
  if (record && record[source]?.length > 0) {
    record[source].forEach(async (element) => {
      if (element.key) await Storage.remove(element.key);
    });
  }
};

export const saveRemoveFile = async (record, data, source, dir) => {
  debugger;
  if (!data[source]) await removeFile(record || {}, data, source);
  else if (data[source] && !data[source].key) {
    if (record && record[source]) await removeFile(record, null, source);

    debugger;
    const { rawFile } = data[source];
    const key = getDirFileKey(rawFile, dir);
    const response = await Storage.put(key, rawFile, {
      level: "public",
      contentType: rawFile.type,
    });

    if (response.key) {
      const nowIso = new Date().toISOString();
      data[source] = {
        bucket: API._options.aws_user_files_s3_bucket,
        region: API._options.aws_user_files_s3_bucket_region,
        key: response.key,
        // type: rawFile.type,
        // createdBy: window.appUser?.fullName,
        createdAt: nowIso,
      };
    } else {
      throw new Error(
        `Storage.put failed: ${
          response.reason?.message || response.reason || response
        }`
      );
    }
  }

  if (data[source]) {
    delete data[source].rawFile;
    delete data[source].src;
    delete data[source].title;
  }
};

/**
 * Uploads new files in data[source] and deletes the ones removed from it
 * (compared with record[source]), then replaces data[source] with the stored
 * file objects. Resolves { removedKeys, uploadedKeys }.
 *
 * With deferDelete, removed files are not deleted here: the caller deletes
 * removedKeys (removeFileKeys) once its save has succeeded, so a save that fails
 * doesn't leave the record pointing at files that are gone.
 */
export const saveRemoveFiles = async (
  record,
  data,
  source,
  dir,
  { deferDelete = false } = {}
) => {
  const deleteList = [];
  const uploadList = [];
  const newFiles = [];
  const removedKeys = [];
  const uploadedKeys = [];
  debugger;

  const dropFile = (key) => {
    removedKeys.push(key);
    if (!deferDelete) deleteList.push(Storage.remove(key));
  };

  if (record && record[source]?.length > 0) {
    record[source].forEach((element) => {
      if (data[source]?.length > 0) {
        if (!data[source].some((itm) => itm.key === element.key))
          dropFile(element.key);
      } else dropFile(element.key);
    });
    await Promise.allSettled(uploadList);
  }

  if (data[source]?.length > 0) {
    data[source].forEach((element) => {
      if (element.rawFile) {
        const key = getDirFileKey(element.rawFile, dir);
        uploadList.push(
          Storage.put(key, element.rawFile, {
            level: "public",
            contentType: element.rawFile.type,
          })
        );
      } else newFiles.push(element);
    });

    const results = await Promise.allSettled(uploadList);

    results.forEach((response) => {
      if (response.status === "fulfilled" && response.value?.key) {
        uploadedKeys.push(response.value.key);
        const nowIso = new Date().toISOString();
        const s3Object = {
          bucket: API._options.aws_user_files_s3_bucket,
          region: API._options.aws_user_files_s3_bucket_region,
          key: response.value.key,
          // type: response.value.contentType,
          // createdBy: window.appUser?.fullName,
          createdAt: nowIso,
        };

        newFiles.push(s3Object);
      } else {
        throw new Error(
          `Storage.put failed: ${
            response.reason?.message || response.reason || response
          }`
        );
      }
    });

    debugger;
    if (newFiles.length > 0) data[source] = newFiles;
    else delete data[source];
  }

  return { removedKeys, uploadedKeys };
};

/** Deletes stored files by key. Never rejects; a failed delete leaves an unused file. */
export const removeFileKeys = (keys = []) =>
  Promise.allSettled(keys.map((key) => Storage.remove(key)));

export const getTemplatePath = (account, source) => {
  if (account && account[source]?.key) {
    return account[source]?.key;
  } else if (window.appUser.reloadYard[source]?.key) {
    return window.appUser.reloadYard[source]?.key;
  } else if (source === "bolTemplate") {
    return `${FilePaths.TEMPLATES}/${FilePaths.TEMPLATE_BOL}`;
  } else if (source === "receiptTemplate") {
    return `${FilePaths.TEMPLATES}/${FilePaths.TEMPLATE_RECEIPT}`;
  }
  return null;
};

export const fetchTemplatePath = async (account, source) => {
  try {
    const defaultTemplateKey =
      source === "bolTemplate"
        ? `${FilePaths.TEMPLATES}/${FilePaths.TEMPLATE_BOL}`
        : source === "receiptTemplate"
        ? `${FilePaths.TEMPLATES}/${FilePaths.TEMPLATE_RECEIPT}`
        : null;

    const reloadYardId =
      window.appUser?.reloadYardId || window.appUser?.reloadYard?.id;

    const [accountResp, reloadYardResp] = await Promise.allSettled([
      account?.id
        ? API.graphql(graphqlOperation(getAccount, { id: account.id }))
        : Promise.resolve(null),
      reloadYardId
        ? API.graphql(graphqlOperation(getReloadYard, { id: reloadYardId }))
        : Promise.resolve(null),
    ]);

    const freshAccount =
      accountResp.status === "fulfilled"
        ? accountResp.value?.data?.getAccount
        : null;
    const freshReloadYard =
      reloadYardResp.status === "fulfilled"
        ? reloadYardResp.value?.data?.getReloadYard
        : null;

    // Update in-memory cache so subsequent calls (and the UI) see the latest.
    if (freshReloadYard && window.appUser) {
      window.appUser.reloadYard = {
        ...(window.appUser.reloadYard || {}),
        ...freshReloadYard,
      };
    }
    if (freshAccount && account) {
      // Mutate the passed account reference (caller typically holds this in component state)
      // so downstream code sees the latest template key.
      account[source] = freshAccount[source];
    }

    const accountKey = freshAccount?.[source]?.key || null;
    if (accountKey) return accountKey;

    const reloadYardKey = freshReloadYard?.[source]?.key || null;
    if (reloadYardKey) return reloadYardKey;

    return defaultTemplateKey;
  } catch (err) {
    logError("fileUtils:fetchTemplatePath", err, false);
    return source === "bolTemplate"
      ? `${FilePaths.TEMPLATES}/${FilePaths.TEMPLATE_BOL}`
      : source === "receiptTemplate"
      ? `${FilePaths.TEMPLATES}/${FilePaths.TEMPLATE_RECEIPT}`
      : null;
  }
};

export const generateSignatureKey = (reloadYardId) => {
  return `${reloadYardId}/sig_${reloadYardId}_${Date.now()}.png`;
};

export const generateFileName = ({ resource, ext, includeDate = false }) => {
  const date = new Date();
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");

  const dateSuffix = includeDate ? `_${day}-${month}-${year}_${hours}` : "";
  const fileName = `${resource}_daily${dateSuffix}.${ext}`;

  return fileName;
};

export const generateAndDownloadDocx = async ({
  templatePath,
  cleanData,
  outputPath,
}) => {
  const response = await API.post("transREST", "/createDocx", {
    body: {
      templateKey: templatePath,
      data: cleanData,
      outputFileKey: outputPath,
    },
  });

  if (response.body?.key) {
    const file = await Storage.get(response.body.key, { download: true });
    downloadFile(file, response.body.key);
  }
};

export const generateAndDownloadPdf = async ({
  templateKey,
  data,
  outputFileKey,
  downloadName,
}) => {
  try {
    const response = await API.post("transREST", "/createPdf", {
      body: {
        templateKey,
        outputFileKey,
        data,
      },
    });

    if (response.body?.key) {
      const file = await Storage.get(response.body.key, { download: true });
      downloadFile(file, downloadName ? downloadName : response.body.key);
    }
  } catch (err) {
    throw err;
  }
};

export const isSameFile = (a, b) => {
  if (a.id && b.id) return a.id === b.id;
  if (a.key && b.key) return a.key === b.key;

  const aName = a.rawFile?.name || a.name;
  const bName = b.rawFile?.name || b.name;
  if (aName && bName) return aName === bName;
  return false;
};

const fnv1a32 = (input) => {
  const str = String(input ?? "");
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    // 32-bit FNV-1a prime multiplication
    hash = (hash * 0x01000193) >>> 0;
  }
  return hash >>> 0;
};

export const isKbExcludedHelpTopic = (topic) => {
  if (!topic) return false;
  const option = HelpTopicOptions?.find((o) => o?.id === topic);
  return option?.kbExclude === true;
};

export const sanitizeHelpPageUrlToFilename = (url) => {
  const raw = String(url ?? "").trim();
  if (!raw) return "";

  const base = raw
    .replace(/[/\\]/g, "_")
    .replace(/[{}]/g, "_")
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 180);

  const hash = fnv1a32(raw).toString(16).padStart(8, "0");
  return `${base || "helpPage"}_${hash}`;
};

export const getHelpPageContentKey = (url) => {
  const fileName = sanitizeHelpPageUrlToFilename(url);
  if (!fileName) return null;
  return `${FilePaths.KB_HELP_PAGES}/${fileName}.md`;
};

export const removeHelpPageContentFile = async (page) => {
  const key = page?.kbContent?.key;
  if (!key) return;
  await Storage.remove(key);
};

export const saveHelpPageContentFile = async (record, data) => {
  const url = data?.url;
  const topic = data?.topic;
  const content = data?.content ?? "";
  const isActive = data?.active !== "false";
  const isLive = data?.isLive === true;
  const shouldHaveKbContentFile = isActive && isLive;

  if (!url) return;
  if (isKbExcludedHelpTopic(topic)) return;

  if (!shouldHaveKbContentFile) {
    const existingKey = record?.kbContent?.key || data?.kbContent?.key;
    if (existingKey) await Storage.remove(existingKey);
    data.kbContent = null;
    return;
  }

  // If URL ever changes, remove the old file and write the new one.
  // (In HelpPageForm, URL is disabled after create, but this keeps it safe.)
  const prevUrl = record?.url;
  if (prevUrl && prevUrl !== url) {
    const oldKey = getHelpPageContentKey(prevUrl);
    if (oldKey) await Storage.remove(oldKey);
  }

  const key = getHelpPageContentKey(url);
  if (!key) return;

  const previousKey = record?.kbContent?.key;
  if (previousKey && previousKey !== key) {
    await Storage.remove(previousKey);
  }

  const blob = new Blob([String(content)], { type: "text/markdown" });
  const response = await Storage.put(key, blob, {
    level: "public",
    contentType: "text/markdown; charset=utf-8",
  });

  if (response?.key) {
    const nowIso = new Date().toISOString();
    data.kbContent = {
      bucket: API._options.aws_user_files_s3_bucket,
      region: API._options.aws_user_files_s3_bucket_region,
      key: response.key,
      createdAt: nowIso,
    };
  }
};

export function csv2arr(str) {
  // https://stackoverflow.com/questions/8493195/how-can-i-parse-a-csv-string-with-javascript-which-contains-comma-in-data
  let line = [""];
  const ret = [line];
  let quote = false;

  for (let i = 0; i < str.length; i++) {
    const cur = str[i];
    const next = str[i + 1];

    if (!quote) {
      const cellIsEmpty = line[line.length - 1].length === 0;
      if (cur === '"' && cellIsEmpty) quote = true;
      else if (cur === ",") line.push("");
      else if (cur === "\r" && next === "\n") {
        line = [""];
        ret.push(line);
        i++;
      } else if (cur === "\n" || cur === "\r") {
        line = [""];
        ret.push(line);
      } else line[line.length - 1] += cur;
    } else {
      if (cur === '"' && next === '"') {
        line[line.length - 1] += cur;
        i++;
      } else if (cur === '"') quote = false;
      else line[line.length - 1] += cur;
    }
  }
  return ret;
}

/**
 * Turn a CSV file's text into header-keyed rows, for the column-mapping import
 * dialogs.
 *
 * Shared by every "upload a spreadsheet and match its columns" flow — the
 * header handling below is where mis-mapped imports come from, and one copy of
 * it means one place to fix them.
 *
 * @param {string} text - the file contents
 * @returns {{columns: {id: string, name: string}[], rows: object[], errors: string[]}}
 *   rows carry a rowIndex, which the preview grid and the error messages both
 *   key on; errors are about the file rather than about any one row.
 */
export const parseCSVTable = (text) => {
  // The whole file goes through one quote-aware pass, header included. Slicing
  // the first line off and splitting it on commas read a quoted header cell
  // ("Business Name, Inc") as two columns, which shifted every field after it
  // one place and mapped the rows against the wrong columns — silently, since
  // the body rows were already parsed correctly and lined up with nothing.
  const table = csv2arr(text ?? "");
  const headerCells = (table[0] || []).map((header) => header.trim());
  // Blank header cells name no field, so they are dropped — but a row's values
  // are still positional, so each kept header carries the column index it was
  // read from. Keying by position in the filtered list instead would shift
  // every field after an interior blank header one column left, and the rows
  // would then be skipped for a missing required value the file has.
  //
  // Repeated header names are numbered apart ("Phone", "Phone (2)"). Rows are
  // keyed by header name, so two columns spelled the same would collapse onto
  // one key: the later column's values would overwrite the earlier column's,
  // the mapping dropdown would offer two entries that select the same data,
  // and the preview would show both columns holding the last one's values. The
  // suffix is only what the user reads and maps against — the column index
  // each header was read from is untouched.
  const usedNames = new Set();
  const headers = headerCells
    .map((name, index) => ({ name, index }))
    .filter((header) => header.name !== "")
    .map((header) => {
      let name = header.name;
      // Keeps counting past a file that already spells a header the suffixed
      // way, so the numbering never lands on a name the file has.
      for (let n = 2; usedNames.has(name); n += 1)
        name = `${header.name} (${n})`;
      usedNames.add(name);
      return { ...header, name };
    });

  if (headers.length === 0)
    return { columns: [], rows: [], errors: ["No column headers found."] };

  const rawRows = table.slice(1).filter((row) => row.join("").trim() !== "");

  let extraColumns = false;

  const rows = rawRows.map((row, index) => {
    const parsed = headers.reduce((acc, header) => {
      acc[header.name] = row[header.index];
      return acc;
    }, {});
    // A row wider than the header line means an unquoted comma inside a value,
    // which silently shifts every field after it into the wrong column.
    // Compared against every header cell, blanks included: a header line that
    // ends in a comma has one more cell than named columns, and its rows are
    // not malformed.
    if (row.length > headerCells.length) extraColumns = true;
    parsed.rowIndex = index;
    return parsed;
  });

  return {
    columns: headers.map((header) => ({ id: header.name, name: header.name })),
    rows,
    errors: extraColumns
      ? ["Some rows have extra unquoted commas or columns."]
      : [],
  };
};

/**
 * A starting column mapping, so a well-named export needs no dropdown work.
 *
 * Only unambiguous header matches are filled in, and a column is claimed by one
 * field only — the user still sees every selection and can change any of it.
 *
 * @param {{id: string}[]} fields - the import's field definitions
 * @param {object} synonyms - {[fieldId]: string[]} letters-only header spellings
 * @param {{id: string}[]} columns - parseCSVTable columns
 * @returns {object} {[fieldId]: csvColumn}
 */
export const guessColumnMap = (fields, synonyms, columns) => {
  // Matched on a letters-only reduction of the header, so "Business Name",
  // "business_name" and "BUSINESSNAME" are one entry.
  const headerKey = (header) =>
    String(header ?? "")
      .toLowerCase()
      .replace(/[^a-z]/g, "");

  const claimed = new Set();
  const map = {};

  (fields ?? []).forEach((field) => {
    const spellings = synonyms?.[field.id] ?? [];
    const match = (columns ?? []).find(
      (column) =>
        column?.id &&
        !claimed.has(column.id) &&
        spellings.includes(headerKey(column.id)),
    );
    if (match) {
      claimed.add(match.id);
      map[field.id] = match.id;
    }
  });

  return map;
};
